import React from 'react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/utils/api';

const WHATSAPP_MESSAGE = "Hi, I'm interested in corporate event services from My Corporate Hub.";

export default function Footer() {
  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    try {
      await api.subscribeNewsletter({ email });
      toast.success('Successfully subscribed to newsletter!');
      e.target.reset();
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    }
  };

  return (
    <footer className="bg-navy py-16">
      <div className="max-w-7xl mx-auto px-4">
        {/* Newsletter Section */}
        <div className="bg-gradient-to-r from-gold/20 to-gold/10 border-2 border-gold/50 rounded-xl p-8 mb-12 shadow-lg">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="text-2xl font-bold text-white mb-2">🔔 Stay Updated with Event Insights</h3>
            <p className="text-slate-200 mb-6">Get exclusive event planning tips, venue updates, and special offers</p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input
                type="email"
                name="email"
                placeholder="Enter your email"
                required
                className="flex-1 bg-white border-gold/30 text-navy placeholder:text-slate-700 h-12"
              />
              <Button type="submit" className="bg-gold hover:bg-gold-light text-navy font-bold whitespace-nowrap px-8 h-12 shadow-md">
                Subscribe Now
              </Button>
            </form>
          </div>
        </div>

        <div className="grid md:grid-cols-5 gap-8">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_07322645-a840-4848-8247-f88636798b30/artifacts/kyqhtx8f_MCH%20Logo-2.png" 
                alt="My Corporate Hub" 
                className="h-12 w-auto"
              />
              <span className="font-heading text-2xl text-white font-semibold">My Corporate Hub</span>
            </div>
            <p className="text-slate-300">India's premier platform for corporate events, conferences, and luxury hospitality.</p>
          </div>
          <div>
            <h4 className="text-gold font-semibold uppercase tracking-wide mb-4">Quick Links</h4>
            <ul className="space-y-3">
              <li><Link to="/services" className="text-slate-300 hover:text-white transition-colors">Services</Link></li>
              <li><Link to="/properties" className="text-slate-300 hover:text-white transition-colors">Properties</Link></li>
              <li><Link to="/gifts" className="text-slate-300 hover:text-white transition-colors">Corporate Gifting</Link></li>
              <li><Link to="/event-permissions" className="text-slate-300 hover:text-white transition-colors">Event Permits</Link></li>
              <li><Link to="/trainers" className="text-slate-300 hover:text-white transition-colors">Trainers</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-gold font-semibold uppercase tracking-wide mb-4">Legal</h4>
            <ul className="space-y-3">
              <li><Link to="/privacy-policy" className="text-slate-300 hover:text-white transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms-conditions" className="text-slate-300 hover:text-white transition-colors">Terms & Conditions</Link></li>
              <li><Link to="/refund-policy" className="text-slate-300 hover:text-white transition-colors">Refund Policy</Link></li>
              <li><Link to="/cancellation-policy" className="text-slate-300 hover:text-white transition-colors">Cancellation Policy</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-gold font-semibold uppercase tracking-wide mb-4">Careers</h4>
            <ul className="space-y-3">
              <li><Link to="/careers" className="text-slate-300 hover:text-white transition-colors">Open Positions</Link></li>
              <li><Link to="/careers#benefits" className="text-slate-300 hover:text-white transition-colors">Why Join Us</Link></li>
              <li><Link to="/careers#culture" className="text-slate-300 hover:text-white transition-colors">Our Culture</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-gold font-semibold uppercase tracking-wide mb-4">Contact Us</h4>
            <ul className="space-y-3 text-slate-300">
              <li className="flex items-start">
                <span className="mr-2">📞</span>
                <span>+91 96648 70766</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✉️</span>
                <span>info@mycorporatehub.com</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">💬</span>
                <a href={`https://wa.me/919664870766?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`} target="_blank" rel="noopener noreferrer" className="hover:text-gold transition-colors">
                  +91 96648 70766
                </a>
              </li>
              <li className="flex items-start">
                <span className="mr-2">📍</span>
                <span>Corporate Office:<br/>L23, Express Zone, A Wing,<br/>Western Express Highway,<br/>Panch Bawadi, Malad East,<br/>Mumbai 400063, Maharashtra, India</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">🏢</span>
                <span>GST: 27AAMCB6393G1Z9</span>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Payment Partner - Razorpay */}
        <div className="border-t border-navy-700 mt-8 pt-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4">
            <span className="text-white text-sm font-medium">Secure Payments Powered By</span>
            <div className="flex items-center gap-4 bg-white px-6 py-3 rounded-lg shadow-lg">
              <img 
                src="https://cdn.razorpay.com/logo.svg" 
                alt="Razorpay" 
                className="h-6"
              />
              <div className="w-px h-8 bg-slate-300"></div>
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg" 
                alt="Visa" 
                className="h-5"
              />
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" 
                alt="Mastercard" 
                className="h-8"
              />
              <div className="w-px h-8 bg-slate-300"></div>
              <img 
                src="https://badges.razorpay.com/badge-light.png" 
                alt="Razorpay Trusted Business" 
                className="h-10"
              />
            </div>
          </div>
        </div>
        
        <div className="border-t border-navy-700 mt-6 pt-8 text-center">
          <p className="text-slate-300 text-sm">© 2025 My Corporate Hub. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
